import { useContext } from 'react';
import { Link, useHistory } from 'react-router-dom';

import { MDText } from 'i18n-react';

import { Button, ChevronRightIcon } from '@motorway/mw-highway-code';

import { GA_VARIABLES } from 'Utilities/analytics';
import { DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsDocumentCapture';
import { EVENT } from 'Utilities/analytics/optimizely/constants';
import { useOptimizelyTrack } from 'Utilities/analytics/optimizely/useOptimizelyTrack';
import { useRetrieveConfirmationSale } from 'Utilities/hooks/useRetrieveConfirmationSale';
import { applyCypressData } from 'Utilities/index';

import { useVehicleCollectionSub } from '../../../vehicleCollection/hooks/useVehicleCollectionSub';
import { useGeneralErrorToast } from '../../../vehicleProfiling/VehicleProfiling.hooks';
import { DocumentCaptureConfirmationProgressContext, DocumentCaptureConfirmationProgressContextType } from '../../DocumentCaptureContexts';
import { captureSentryException } from '../../DocumentCaptureError/captureSentryException';
import { displayToast } from '../../DocumentCaptureError/displayToast';
import { useConfirmSale } from '../hooks/useConfirmSale';
import { useOfferSummary } from '../hooks/useOfferSummary';

import LocalTexts from './Footer.texts.json';

import styles from './Footer.styles.scss';

const LocalT = new MDText(LocalTexts);

export const Footer = () => {
  const history = useHistory();
  const { OPTIMIZELY_TRACK } = useOptimizelyTrack();
  const { offer: { acceptedBidValue, closingBidId, offerId: enquiryId }, vehicle: { vrm } } = useOfferSummary();
  const { showErrorToast } = useGeneralErrorToast();
  const { confirmSale, isLoading } = useConfirmSale({
    onError: () => {
      showErrorToast({
        content: LocalT.translate('confirmSaleErrorToast.message'),
        heading: LocalT.translate('confirmSaleErrorToast.title'),
        persistent: true,
      });
    },
  });
  const { serviceFee } = useVehicleCollectionSub();

  const {
    setProgress,
  } = useContext<DocumentCaptureConfirmationProgressContextType>(DocumentCaptureConfirmationProgressContext);

  const handleRetrieveConfirmationSaleSuccess = (data: DocumentCaptureConfirmationProgressContextType['progress']) => {
    if (setProgress) {
      setProgress(data);
    }
  };

  const handleRetrieveConfirmationSaleError = (err: unknown) => {
    displayToast(err);
    captureSentryException(err);
  };

  const { loading: confirmSaleLoad, retrieveConfirmationSale } = useRetrieveConfirmationSale({
    onError: handleRetrieveConfirmationSaleError,
    onSuccess: handleRetrieveConfirmationSaleSuccess,
  });

  const bidValue = String(acceptedBidValue);
  const vehicleDetailsUrl = `/${vrm}/vehicle-details`;

  const onVehicleDetailsClick = () => {
    DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.VEHICLE_DETAILS_LINK_CLICKED({ buttonLabel: 'vehicle details' });
  };

  const onClickHandler = async () => {
    await confirmSale({ closingBidId: closingBidId!, enquiryId: enquiryId! });

    GA_VARIABLES.offerAcceptedValue = bidValue;
    DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.CONFIRM_SALE_BUTTON_CLICKED({ label: 'Complete my sale' });
    DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.CONFIRM_SALE_BUTTON_ACCEPTED_VALUE({ label: bidValue });
    OPTIMIZELY_TRACK(EVENT.PS_COMPLETE_SALE_CTA_CLICKED);
    OPTIMIZELY_TRACK(EVENT.PS_COMPLETE_SALE_CTA_CLICKED, { customId: String(enquiryId) });

    await retrieveConfirmationSale();
    history.push(`/${vrm}/add-bank-details`);
  };

  const footerText = serviceFee?.feeAmountGBP ? 'infoWithFee' : 'info';

  return (
    <div className={styles.footer} data-main-override="true">
      <div className={styles.content}>
        <div className={styles.info}>
          {LocalT.translate(footerText, {
            vehicleDetailsLink: (
              <Link className={styles.link} onClick={onVehicleDetailsClick} target='_blank' to={vehicleDetailsUrl}>
                {LocalT.translate('link')}
              </Link>
            ),
          })}
        </div>
        <div className={styles.confirmButton}>
          <Button fullWidth {...applyCypressData('completeMySaleButton')} disabled={!enquiryId || !closingBidId} icon={ChevronRightIcon} label={LocalT.translate('confirmButton')}
            loading={isLoading || confirmSaleLoad} onClick={onClickHandler} />
        </div>
      </div>
    </div>
  );
};
